// frontend/src/pages/MfaChallengePage.jsx
import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import Spinner from "../components/Spinner";

/**
 * MfaChallengePage — second step of an MFA-gated login.
 *
 * Reached only via redirect from LoginPage, which passes the short-lived
 * ``mfaToken`` in router state (never the URL). The user enters a 6-digit TOTP
 * code or a one-time backup code; on success a full session is established and
 * we continue to /codes. Loading this route directly (no token in state) sends
 * the user back to /login.
 */
function MfaChallengePage() {
  const { completeMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const mfaToken = location.state?.mfaToken;

  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // No token in router state → this page was opened out of flow.
  if (!mfaToken) {
    return <Navigate to="/login" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setSubmitting(true);
    try {
      await completeMfa(mfaToken, code.trim());
      navigate("/codes", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error("That code didn't work. Try again or use a backup code.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-md p-6 sm:p-8">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Two-factor authentication</h1>
          <p className="mt-1 text-sm text-gray-500">
            Enter the 6-digit code from your authenticator app, or a backup code.
          </p>
        </div>

        <form onSubmit={onSubmit} noValidate className="space-y-5">
          <div>
            <label
              htmlFor="mfa-code"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Authentication code
            </label>
            <input
              id="mfa-code"
              type="text"
              inputMode="text"
              autoComplete="one-time-code"
              autoFocus
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {submitting && <Spinner size={16} className="text-white" />}
            {submitting ? "Verifying…" : "Verify"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default MfaChallengePage;
